import { useState } from 'react'
import { useMutation } from '@tanstack/react-query'
import { Link } from 'react-router-dom'
import { t } from '@/i18n'
import { commitImport, previewImport, type ImportPreviewOut } from '@/lib/api'

const FIELD_KEYS = [
  { key: 'external_order_id' },
  { key: 'occurred_at', required: true },
  { key: 'side', required: true },
  { key: 'asset' },
  { key: 'quantity', required: true },
  { key: 'price', required: true },
  { key: 'fiat' },
  { key: 'fiat_amount' },
  { key: 'counterparty' },
  { key: 'payment_method' },
  { key: 'order_status' },
] as const

export function ImportPage() {
  const [file, setFile] = useState<File | null>(null)
  const [preview, setPreview] = useState<ImportPreviewOut | null>(null)
  const [mapping, setMapping] = useState<Record<string, string>>({})
  const [exchange, setExchange] = useState('binance')
  const [result, setResult] = useState<string | null>(null)
  const [pickError, setPickError] = useState<string | null>(null)

  const previewMutation = useMutation({
    mutationFn: (next: File) => previewImport(next),
    onSuccess: (data) => {
      setPreview(data)
      setMapping(data.mapping)
      if (data.exchange_hint) setExchange(data.exchange_hint)
      setResult(null)
    },
  })

  const commitMutation = useMutation({
    mutationFn: () => {
      if (!file || !preview) throw new Error(t('imp.needFile'))
      return commitImport({
        file,
        exchange_code: exchange,
        delimiter: preview.delimiter,
        mapping,
        skip_duplicates: true,
      })
    },
    onSuccess: (data) => {
      setResult(
        t('imp.created', { c: data.created, d: data.skipped_duplicates, f: data.failed }),
      )
    },
  })

  return (
    <div className="mx-auto max-w-5xl px-4 py-5 md:px-8 md:py-8">
      <h1 className="text-xl font-semibold text-slate-100">{t('imp.title')}</h1>
      <p className="mt-1 text-sm text-slate-500">
        {t('imp.lead')} {t('imp.leadMid')}{' '}
        <Link to="/screenshots" className="text-slate-300 underline">
          {t('imp.shotsLink')}
        </Link>
        . {t('imp.leadEnd')}{' '}
        <Link to="/transactions" className="text-slate-300 underline">
          {t('imp.txLink')}
        </Link>
        .
      </p>

      <input
        type="file"
        accept=".csv,.txt,text/csv,text/plain"
        className="mt-5 text-sm"
        onChange={(event) => {
          const next = event.target.files?.[0]
          if (!next) return
          const name = next.name.toLowerCase()
          if (/\.(jpe?g|png|webp|gif|heic|bmp|pdf|xlsx|xlsm|xls)$/.test(name)) {
            setFile(null)
            setPreview(null)
            setResult(null)
            setPickError(
              name.endsWith('.pdf') || /\.(xlsx|xlsm|xls)$/.test(name)
                ? t('imp.wrongPdf')
                : t('imp.wrongImg'),
            )
            previewMutation.reset()
            return
          }
          setFile(next)
          setResult(null)
          setPickError(null)
          previewMutation.mutate(next)
        }}
      />

      {(pickError || previewMutation.isError) && (
        <p className="mt-3 text-sm text-loss">
          {pickError ??
            (previewMutation.error instanceof Error
              ? previewMutation.error.message
              : t('imp.readFail'))}
        </p>
      )}

      {preview && (
        <>
          <div className="mt-6 grid gap-4 sm:grid-cols-2">
            <label className="text-xs text-slate-400">
              {t('common.exchange')}
              <select
                value={exchange}
                onChange={(event) => setExchange(event.target.value)}
                className="mt-1 w-full rounded-md border border-surface-border bg-surface px-3 py-2 text-sm"
              >
                <option value="binance">Binance</option>
                <option value="bybit">Bybit</option>
                <option value="other">{t('imp.other')}</option>
              </select>
            </label>
            <div className="text-xs text-slate-500">
              {t('imp.delim')} <span className="num text-slate-300">{preview.delimiter === '\t' ? 'TAB' : preview.delimiter}</span>
              <br />
              {t('imp.rows')} <span className="num text-slate-300">{preview.row_count}</span>
            </div>
          </div>

          {preview.issues.length > 0 && (
            <ul className="mt-3 list-disc pl-5 text-sm text-pending">
              {preview.issues.map((issue) => (
                <li key={issue}>{issue}</li>
              ))}
            </ul>
          )}

          <h2 className="mt-6 text-sm font-medium text-slate-300">{t('imp.map')}</h2>
          <div className="mt-2 grid gap-2 sm:grid-cols-2">
            {FIELD_KEYS.map((field) => (
              <label key={field.key} className="text-xs text-slate-400">
                {t(`imp.f.${field.key}`)}
                {'required' in field && field.required ? ' *' : ''}
                <select
                  value={mapping[field.key] ?? ''}
                  onChange={(event) =>
                    setMapping((prev) => ({ ...prev, [field.key]: event.target.value }))
                  }
                  className="mt-1 w-full rounded-md border border-surface-border bg-surface px-2 py-1.5 text-sm"
                >
                  <option value="">{t('imp.unmap')}</option>
                  {preview.headers.map((header) => (
                    <option key={header} value={header}>
                      {header}
                    </option>
                  ))}
                </select>
              </label>
            ))}
          </div>

          <h2 className="mt-6 text-sm font-medium text-slate-300">{t('imp.sample')}</h2>
          <div className="mt-2 overflow-auto rounded-md border border-surface-border">
            <table className="min-w-full text-xs">
              <thead className="text-left text-slate-500">
                <tr>
                  {preview.headers.slice(0, 8).map((header) => (
                    <th key={header} className="px-2 py-1">
                      {header}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {preview.sample.slice(0, 8).map((row, index) => (
                  <tr key={index} className="border-t border-surface-border/60">
                    {preview.headers.slice(0, 8).map((header) => (
                      <td key={header} className="px-2 py-1 text-slate-300">
                        {String((row._raw as Record<string, string> | undefined)?.[header] ?? '')}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <button
            type="button"
            className="mt-5 rounded-md bg-slate-100 px-4 py-2 text-sm text-slate-900 disabled:opacity-50"
            disabled={commitMutation.isPending}
            onClick={() => commitMutation.mutate()}
          >
            {commitMutation.isPending ? t('imp.going') : t('imp.go')}
          </button>
          {commitMutation.isError && (
            <p className="mt-3 text-sm text-loss">
              {commitMutation.error instanceof Error ? commitMutation.error.message : t('imp.fail')}
            </p>
          )}
          {result && <p className="mt-3 text-sm text-profit">{result}</p>}
          {commitMutation.data && commitMutation.data.errors.length > 0 && (
            <ul className="mt-2 list-disc pl-5 text-xs text-loss">
              {commitMutation.data.errors.map((item) => (
                <li key={item}>{item}</li>
              ))}
            </ul>
          )}
        </>
      )}
    </div>
  )
}
